import { useEffect, useMemo, useState } from 'react';
import { fetchCloudTalkRecordingBlob } from '../../lib/cloudTalkApi';

type CloudTalkRecordingPlayerProps = {
  callIds: string[];
};

type SlotState = {
  callId: string;
  status: 'idle' | 'loading' | 'ready' | 'error';
  url: string | null;
  error: string | null;
};

/**
 * Loads CloudTalk WAV on demand (Management / IT only — gate in parent).
 * Does not persist audio in Supabase.
 */
export function CloudTalkRecordingPlayer({ callIds }: CloudTalkRecordingPlayerProps) {
  const ids = useMemo(
    () => [...new Set(callIds.map((id) => String(id || '').trim()).filter((id) => /^\d+$/.test(id)))],
    [callIds],
  );

  const [slots, setSlots] = useState<SlotState[]>([]);

  useEffect(() => {
    setSlots(
      ids.map((callId) => ({
        callId,
        status: 'idle',
        url: null,
        error: null,
      })),
    );
    return () => {
      /* revoke on unmount below via next effect cleanup */
    };
  }, [ids]);

  useEffect(() => {
    return () => {
      for (const slot of slots) {
        if (slot.url) URL.revokeObjectURL(slot.url);
      }
    };
    // Only revoke when slots identity changes / unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots]);

  const loadOne = async (callId: string) => {
    setSlots((prev) =>
      prev.map((slot) =>
        slot.callId === callId
          ? { ...slot, status: 'loading', error: null }
          : slot,
      ),
    );
    try {
      const blob = await fetchCloudTalkRecordingBlob(callId);
      const url = URL.createObjectURL(blob);
      setSlots((prev) =>
        prev.map((slot) => {
          if (slot.callId !== callId) return slot;
          if (slot.url) URL.revokeObjectURL(slot.url);
          return { callId, status: 'ready', url, error: null };
        }),
      );
    } catch (e) {
      setSlots((prev) =>
        prev.map((slot) =>
          slot.callId === callId
            ? {
                callId,
                status: 'error',
                url: null,
                error: e instanceof Error ? e.message : 'Failed to load recording.',
              }
            : slot,
        ),
      );
    }
  };

  if (ids.length === 0) {
    return (
      <p className="dem-cloudtalk-detail__ai-empty">
        This row has no CloudTalk call id, so recording cannot be loaded.
      </p>
    );
  }

  return (
    <div className="dem-cloudtalk-recording-list">
      {slots.map((slot, index) => (
        <div key={slot.callId} className="dem-cloudtalk-recording-card">
          <div className="dem-cloudtalk-recording-card__head">
            <span className="dem-cloudtalk-detail-field__label">
              {ids.length > 1 ? `Recording ${index + 1}` : 'Recording'}
              <span className="dem-cloudtalk-dem-muted"> · call {slot.callId}</span>
            </span>
            {slot.status === 'idle' || slot.status === 'error' ? (
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => void loadOne(slot.callId)}
              >
                {slot.status === 'error' ? 'Retry' : 'Load recording'}
              </button>
            ) : null}
          </div>
          {slot.status === 'loading' ? (
            <p className="dem-cloudtalk-detail__ai-empty">Loading recording from CloudTalk…</p>
          ) : null}
          {slot.status === 'error' ? (
            <p className="dem-cloudtalk-detail__ai-empty">{slot.error}</p>
          ) : null}
          {slot.status === 'ready' && slot.url ? (
            <audio className="dem-cloudtalk-recording-player" controls preload="metadata" src={slot.url} />
          ) : null}
        </div>
      ))}
    </div>
  );
}
